import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

import ButtonLink from 'components/ButtonLink'
import Loader from 'components/Loader'
import Page from 'components/Page'
import RichText from 'components/RichText'
import ROUTES from 'config/routes.json'
import { useScrollToTopOnRouteChange } from 'hooks/useScrollToTopOnRouteChange'
import common from 'prismic-translates/common.json'
import content from 'prismic-translates/dialog-sc-opt-out-arbitration.json'
import Recaptcha from 'src/components/Recaptcha'
import FooterPortal from 'src/containers/Layouts/Layout/FooterPortal'
import { useAppSelector } from 'src/store/hooks'
import { isAppInitializedSelector } from 'src/store/modules/appConfig/selectors'
import { isLoggedInSelector } from 'store/modules/user/selectors'

import OptOutButton from './components/OptOutButton'
import classes from './styles.module.scss'
import type { SCRequestCodeDialogStep } from './types'
import { useOptOutArbitrationRequest } from './utils/useOptOutArbitrationRequest'

const t = getTranslationFromJson([content, common])

export default function OptOutArbitrationPage() {
  const loggedIn = useAppSelector(isLoggedInSelector)
  const appInitialized = useAppSelector(isAppInitializedSelector)
  const [step, setStep] = useState<SCRequestCodeDialogStep>('1_RECAPCHA_STEP')
  const optOutArbitrationRequest = useOptOutArbitrationRequest()
  const pathname = usePathname()

  useScrollToTopOnRouteChange()

  const goToRequestButtonStep = () => {
    setStep('2_SHOW_BUTTON_STEP')
  }

  const optOutRequesSentStep = () => {
    optOutArbitrationRequest.send()
    setStep('3_SEND_REQUEST_STEP')
  }

  if (!appInitialized) {
    return null
  }

  return (
    <>
      <Page className={classes.root}>
        <h5 className={`mt-typography-h5 ${classes.title}`}>{t('page_title')}</h5>
        <div className={classes.content}>
          {loggedIn ? (
            <>
              <p className={`mt-typography-body1 ${classes.text}`}>{t('page_msg_prt1')}</p>
              <div className={classes.step}>
                {(() => {
                  switch (step) {
                    case '1_RECAPCHA_STEP':
                      return (
                        <>
                          <p className='mt-typography-body1'>{t('step2')}</p>
                          <Recaptcha verifyCallback={goToRequestButtonStep} className={classes.recaptcha} />
                        </>
                      )
                    case '2_SHOW_BUTTON_STEP':
                      return <OptOutButton onClick={optOutRequesSentStep} />
                    case '3_SEND_REQUEST_STEP':
                      if (optOutArbitrationRequest.loading) {
                        return <Loader />
                      }

                      if (!optOutArbitrationRequest.isSent || optOutArbitrationRequest.rejected) {
                        return null
                      }

                      return (
                        <>
                          <p className={`mt-typography-body1 ${classes.text}`}>{t('page_msg_prt2')}</p>
                          <p className='mt-typography-body1 mt-typography-alignCenter'>{t('step3')}</p>
                        </>
                      )
                    default:
                      return null
                  }
                })()}
              </div>
            </>
          ) : (
            <>
              <p className={`mt-typography-body1 ${classes.text}`}>{t('logout_description_msg')}</p>
              <ul className={classes.list}>
                <li>
                  <p className={`mt-typography-body1 ${classes.text}`}>{t('logout_submenu_login')}</p>
                  <div className={classes.buttonWrapper}>
                    <ButtonLink
                      className={classes.loginButton}
                      href={`${ROUTES.LOGIN}?from=${encodeURIComponent(`${pathname}`)}`}
                    >
                      {t('login')}
                    </ButtonLink>
                  </div>
                </li>
                <li className={classes.submenuOptOut}>
                  <RichText field={t('logout_submenu_opt_out_click')} />
                </li>
              </ul>
            </>
          )}
        </div>
      </Page>
      <FooterPortal />
    </>
  )
}
